import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalStore } from '../stores/globalStore';
import { useNotificationStore } from '../stores/notificationStore';
import { useFeatureVisibilityStore } from '../stores/featureVisibilityStore';
import { useConnectionStore } from '../stores/connectionStore';
import { recordRecentFacility } from '../utils/recentFacilities';
import { markDeliberateFacilitySwitch } from '../utils/facilityChangeIntent';

// Recently-used facilities live in utils/recentFacilities.ts (per connection). Re-exported here
// for the callers that already import them from this hook.
export { getRecentFacilities, recordRecentFacility, type RecentFacility } from '../utils/recentFacilities';

// ============================================================================
// Facility Switcher Hook
// ============================================================================

/**
 * Hook that provides a facility switch function which:
 * 1. Records the facility as recently used (for this connection) and as the user's own choice
 *    (utils/facilityChangeIntent.ts: pages pinned to a facility follow it without a notice)
 * 2. Clears facility settings from the global store
 * 3. Invalidates all React Query caches (facility-specific data)
 * 4. Sets a brief switching state for the loading overlay
 * 5. Shows a success notification
 *
 * User preferences (theme, column widths, etc.) are preserved across switches
 * because they are keyed by userId, not facilityId.
 */
export function useFacilitySwitcher() {
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  const switchFacility = useCallback(
    async (facilityId: number, facilityName: string) => {
      const globalStore = useGlobalStore.getState();
      const prevFacilityId = globalStore.facilityId;

      // Skip if same facility
      if (facilityId === prevFacilityId) return;

      // Mark as switching (shows overlay)
      globalStore.setIsSwitchingFacility(true);

      // Record the NEW facility as recently used in this connection
      recordRecentFacility(
        globalStore.userId,
        useConnectionStore.getState().selectedConnection?.connectionId,
        facilityId,
        facilityName,
      );

      // The user chose this one: a page pinned to the old facility follows quietly.
      markDeliberateFacilitySwitch(facilityId);

      // Clear facility settings so they reload for the new facility
      globalStore.clearFacilitySettings();

      // Select the new facility (persists to sessionStorage)
      globalStore.selectFacility(facilityId, facilityName);

      // Sync feature visibility for the new facility
      if (globalStore.userId > 0) {
        useFeatureVisibilityStore.getState().syncWithUser(globalStore.userId, facilityId);
      }

      // Invalidate all React Query caches.
      // Most query keys include facilityId, so invalidating everything ensures
      // no stale data from the previous facility leaks through.
      await queryClient.invalidateQueries();

      // Also remove cached data for queries that won't automatically refetch
      // (e.g., inactive queries with old facilityId in their key)
      queryClient.removeQueries({
        predicate: (query) => {
          // Remove any query whose key contains the previous facility ID
          const keyStr = JSON.stringify(query.queryKey);
          return keyStr.includes(String(prevFacilityId));
        },
      });

      // Brief delay so the overlay is visible and data starts refetching
      await new Promise((resolve) => setTimeout(resolve, 400));

      // Clear switching state
      globalStore.setIsSwitchingFacility(false);

      addNotification({
        message: `Switched to ${facilityName}`,
        type: 'success',
        duration: 3000,
      });
    },
    [queryClient, addNotification],
  );

  return { switchFacility };
}
